"use client";

import { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

import { track } from "@vercel/analytics";
import { toast } from "sonner";
import { Check, Copy, Smartphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { exitFullscreen } from "@/lib/fullscreen";

interface SharePopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
}

export function SharePopup({ open, onOpenChange, sessionId }: SharePopupProps) {
  const [shareUrl, setShareUrl] = useState<string>("");
  const [copied, setCopied] = useState(false);
  

  const handleCopyLink = async () => {
    if (shareUrl) {
      try {
        track("copy_share_link");
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
        toast.error("Failed to copy link");
      }
    }
  };

  useEffect(() => {
    if (open && sessionId) {
      // Check for custom APP_URL first, then fall back to VERCEL_URL, then window.location.origin
      const baseUrl =
        process.env.NEXT_PUBLIC_APP_URL ||
        (process.env.NEXT_PUBLIC_VERCEL_URL
          ? `https://${process.env.NEXT_PUBLIC_VERCEL_URL}`
          : typeof window !== "undefined"
          ? window.location.origin
          : "");
      
      const url = new URL(baseUrl);
      url.searchParams.set("referrer", sessionId);
      setShareUrl(url.toString());
    }
  }, [open, sessionId]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        void exitFullscreen();
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange]
  );

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent
        className="!inset-0 !top-0 !mt-0 !h-dvh !max-h-dvh !w-full !rounded-none"
        showHandle={false}
        aria-describedby={undefined}
      >
        <DrawerHeader className="border-b px-4 pb-3 pt-drawer-safe-top">
          <div className="flex items-center gap-2">
            <div className="w-10 shrink-0" aria-hidden />
            <DrawerTitle className="min-w-0 flex-1 text-center text-xl">Share the Hunt</DrawerTitle>
            <div className="flex w-10 shrink-0 items-center justify-end">
              <button
                type="button"
                onClick={() => handleOpenChange(false)}
                className="rounded-full p-2 hover:bg-muted active:bg-muted"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </button>
            </div>
          </div>
        </DrawerHeader>

        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 overflow-auto bg-[hsl(210,40%,98%)] p-6 md:flex-row md:items-center md:gap-12 md:px-12 md:py-8">
          {/* QR Code */}
          {shareUrl && (
            <div className="flex shrink-0 items-center justify-center">
              <div className="aspect-square w-[min(280px,80vw)] rounded-xl bg-white p-3 md:w-[min(350px,calc(100vh-200px))]" role="img" aria-label="QR code to join the scavenger hunt">
                <QRCodeSVG
                  value={shareUrl}
                  size={1000}
                  level="M"
                  includeMargin={false}
                  bgColor="#ffffff"
                  fgColor="#1e3a5f"
                  className="h-full w-full"
                  aria-hidden="true"
                />
              </div>
            </div>
          )}

          {/* Instructions and button */}
          <div className="flex w-full shrink-0 flex-col items-center gap-4 md:flex-1 md:items-start md:gap-6">
            <div className="flex flex-col items-center gap-2 text-center md:items-start md:text-left">
              <div className="flex items-center gap-2">
                <Smartphone className="h-5 w-5 shrink-0 text-primary md:h-6 md:w-6" />
                <p className="text-xl font-semibold text-foreground md:text-2xl">
                  Scan with a mobile device
                </p>
              </div>
              <p className="text-base text-muted-foreground md:text-lg">
                Open your phone&apos;s camera app and point it at the QR code to join the hunt.
              </p>
            </div>

            {/* Copy link button */}
            {shareUrl && (
              <Button
                variant="outline"
                onClick={handleCopyLink}
                className="h-12 w-full text-base font-semibold md:h-14 md:w-auto md:px-6 md:text-lg"
              >
                {copied ? (
                  <>
                    <Check className="h-5 w-5" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-5 w-5" />
                    Tap to copy link
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
